import DOMPurify from "dompurify"
import { setTopologyId } from "@/services/logger"
import { TOPOLOGY_SANITIZE } from "@/constants/topology"
import logger from "@/services/logger"
import {
  INPUT_SANITIZE_START,
  INPUT_SANITIZE_COMPLETE,
  XSS_PAYLOAD_DETECTED,
  XSS_PAYLOAD_SANITIZED,
} from "@/constants/events"

// Set topology ID for semantic logging
setTopologyId(TOPOLOGY_SANITIZE)

// LLM:METADATA
// :topology: [Domain:Security | Role:Sanitization | Pattern:Middleware]
// :relates-to: uses: "DOMPurify", required-by: "All Components"
// :rationale: "Prevent XSS attacks by sanitizing all user input before rendering"
// :references: PRD: "Security Requirements", SPEC: "SPEC-SEC-001"
// :contract: pre: "Input is string", post: "Output is sanitized HTML", invariant: "No script tags in output" (evidence: "SANITIZE_INPUT_COMPLETE")
// :decision_cache: "Chose DOMPurify over custom sanitization for proven security track record [SEC-001]"
// LLM:END

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} html - HTML content to sanitize
 * @returns {string} - Sanitized HTML
 */
export function sanitizeHtml(html) {
  if (typeof html !== "string") {
    return ""
  }

  logger.info(INPUT_SANITIZE_START, "Starting HTML sanitization", {
    inputLength: html?.length,
  })

  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["b", "i", "u", "strong", "em", "p", "br"],
    ALLOWED_ATTR: [],
  })

  // Check for XSS payload patterns
  const xssPatterns = [
    /<script\b[^>]*>([\s\S]*?)<\/script>/gi,
    /<iframe\b[^>]*>([\s\S]*?)<\/iframe>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
  ]

  const hasXssPayload = xssPatterns.some((pattern) => pattern.test(html))

  if (hasXssPayload) {
    logger.warn(XSS_PAYLOAD_DETECTED, "XSS payload detected in input", {
      inputLength: html?.length,
      patternsDetected: xssPatterns.length,
    })
  } else {
    logger.info(XSS_PAYLOAD_SANITIZED, "XSS payload sanitized successfully", {
      inputLength: html?.length,
      outputLength: sanitized?.length,
    })
  }

  logger.info(INPUT_SANITIZE_COMPLETE, "HTML sanitization completed", {
    inputLength: html?.length,
    outputLength: sanitized?.length,
  })

  return sanitized
}
